import { ChangeEvent, InputHTMLAttributes, Ref, useEffect, useRef } from 'react';

import { map } from '@flipster/utils/map';
import Num, { inputFormatter, numberFormatter } from '@flipster/utils/num';

import { useMergeRef } from '../misc/ref/use-merge-ref';
import { useControllableState } from '../misc/state/use-controllable-state';

export interface FormattedInputProps<T>
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'type'> {
  ref?: Ref<HTMLInputElement>;
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
  formatter?: (value: T) => string;
  deformatter?: (value: string) => T;
}

export const FormattedInput = <T,>({
  value,
  defaultValue = value,
  onChange: onChangeProp,
  formatter = (v) => String(v),
  deformatter = (v) => v as T,
  ref,
  ...props
}: FormattedInputProps<T>) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const mergedRef = useMergeRef(inputRef, ref);
  const prevCursor = useRef<number>(0);
  const [innerValue, setInnerValue] = useControllableState({
    value: map(value, formatter),
    defaultValue: map(defaultValue, formatter) ?? '',
    onChange(nextValue) {
      onChangeProp?.(deformatter(nextValue));
    },
  });

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { selectionStart } = e.currentTarget;
    let { selectionEnd } = e.currentTarget;
    let targetValue = e.currentTarget.value;
    const isSingleCursor = selectionEnd != null && selectionStart === selectionEnd;
    if (targetValue == null || targetValue === '') {
      setInnerValue('');
      return;
    }

    const deformattedValue = deformatter(targetValue);
    let formattedValue = formatter(deformattedValue);
    if (props.maxLength && formattedValue.length > props.maxLength) {
      e.preventDefault();
      return;
    }

    // invalid input for formatter
    if (
      selectionEnd != null &&
      isSingleCursor &&
      targetValue.length > (innerValue?.length ?? 0) &&
      formattedValue === innerValue
    ) {
      // oxlint-disable-next-line no-param-reassign
      e.currentTarget.value = formattedValue;
      const recoverableCursor = selectionEnd - (targetValue.length - formattedValue.length);
      e.currentTarget.setSelectionRange(recoverableCursor, recoverableCursor);
      return;
    }

    if (
      selectionEnd != null &&
      isSingleCursor &&
      targetValue.length < (innerValue?.length ?? 0) &&
      deformattedValue === map(innerValue, deformatter)
    ) {
      const iterable = [...formattedValue];
      iterable.splice(selectionEnd - 1, 1);
      const nextValue = formatter(deformatter(iterable.join('')));
      selectionEnd += nextValue.length - formattedValue.length + 1;
      formattedValue = nextValue;
      // oxlint-disable-next-line no-param-reassign
      e.currentTarget.value = formattedValue;
      targetValue = e.currentTarget.value;
    }
    prevCursor.current = (selectionEnd ?? 0) + (formattedValue.length - targetValue.length);
    setInnerValue(formattedValue);
  };

  useEffect(() => {
    if (innerValue != null && innerValue.length > 0 && prevCursor.current !== inputRef.current?.selectionEnd) {
      inputRef.current?.setSelectionRange(prevCursor.current, prevCursor.current);
    }
  }, [innerValue]);

  return <input type='text' ref={mergedRef} value={innerValue} onChange={onChange} {...props} />;
};

function checkOverMax(val?: string, max?: string): boolean {
  return val != null && max != null && Num(val).isGreaterThan(max);
}

export interface FormattedNumberInputProps extends Omit<FormattedInputProps<string>, 'formatter' | 'deformatter'> {
  precision?: number;
  max?: string;
}

export const FormattedNumberInput = ({ precision, max, onChange, ...props }: FormattedNumberInputProps) => {
  const ref = useRef<HTMLInputElement>(null);

  return (
    <FormattedInput
      ref={ref}
      onChange={(nextValue) => {
        if (checkOverMax(nextValue, max)) {
          onChange?.(numberFormatter(max, { withComma: false, precision }));
          ref.current?.setSelectionRange(-1, -1);
          return;
        }
        onChange?.(nextValue);
      }}
      formatter={(val) => inputFormatter(val, { withComma: true, precision })}
      deformatter={(val) => val.replace(/[^\d.]/g, '')}
      maxLength={12}
      inputMode='numeric'
      autoCapitalize='off'
      autoComplete='off'
      autoCorrect='off'
      spellCheck='false'
      data-lpignore='true'
      {...props}
    />
  );
};
